import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  Bell, Package, Clock, Users, ClipboardList, Calendar, Building2,
  Check, X, Loader2, Play, Truck
} from 'lucide-react'
import { ErrorState } from '@comforthub/design-system'
import { api } from '../../lib/api'
import { toArray } from '../../lib/page'
import { useAuth } from '../../contexts/AuthContext'
import {
  isDynamicNotificationId,
  mergeDismissedIds,
  orderIdFromNotification,
  persistDismissedIds,
  resolveTaskPath,
} from '../../lib/smartNotifications'

interface Notification {
  id: string
  title: string
  internalMessage?: string
  externalMessage?: string
  thing?: string
  thingType?: string
  workflowCode?: string
  errorCode?: number
  isViewed?: boolean
  company?: string
  createdAt?: string
  link?: string
  metadata?: {
    storeId?: string
    inventoryId?: string
    quantity?: number
    [key: string]: any
  }
}

function readDismissed(): string[] {
  try {
    const saved = localStorage.getItem('ch_dismissed_notifications')
    const parsed = saved ? JSON.parse(saved) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export default function NotificationsDropdown() {
  const { activePersona } = useAuth()
  const isPlatformAdminView = activePersona === 'Admin'
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [dismissingAll, setDismissingAll] = useState(false)
  const [localDismissed, setLocalDismissed] = useState<string[]>(readDismissed)
  // The last load decides the empty state: an empty list after a failed load
  // means "we don't know", not "All caught up!".
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'failed'>('loading')
  const [retrying, setRetrying] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const fetchNotifications = async () => {
    try {
      const path = isPlatformAdminView ? '/admin/notifications' : '/notifications'
      const res = await api.get<Notification[]>(path)
      setNotifications(toArray<Notification>(res.data))
      setLoadState('loaded')
    } catch (err) {
      console.error('Failed to fetch notifications', err)
      setLoadState('failed')
    }
  }

  const retryLoad = async () => {
    setRetrying(true)
    await fetchNotifications()
    setRetrying(false)
  }

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [isPlatformAdminView])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const sync = () => setLocalDismissed(readDismissed())
    window.addEventListener('storage', sync)
    return () => window.removeEventListener('storage', sync)
  }, [])

  const activeNotifications = notifications.filter(n => {
    if (n.isViewed) return false
    if (localDismissed.includes(n.id)) return false
    return true
  })

  const dismissIds = (ids: string[]) => {
    if (ids.length === 0) return
    setLocalDismissed(prev => {
      const updated = mergeDismissedIds(prev, ids)
      persistDismissedIds(updated)
      return updated
    })
    const idSet = new Set(ids)
    setNotifications(prev => prev.filter(n => !idSet.has(n.id)))
  }

  const handleDismiss = async (id: string) => {
    setActionError(null)
    if (isDynamicNotificationId(id)) {
      dismissIds([id])
      return
    }
    // Stored notification (rare): no dedicated view API for merchant feed —
    // hide locally so the UI still responds.
    dismissIds([id])
    try {
      await api.post(`/notifications/${id}/view`)
    } catch (err) {
      console.error('Failed to persist notification view', err)
    }
  }

  const handleDismissAll = () => {
    setActionError(null)
    setDismissingAll(true)
    try {
      dismissIds(activeNotifications.map(n => n.id))
    } finally {
      setDismissingAll(false)
    }
  }

  const handleQuickRestock = async (notificationId: string, storeId: string, inventoryId: string, currentQty: number) => {
    setActionLoadingId(notificationId)
    setActionError(null)
    try {
      await api.put(`/stock/${storeId}/${inventoryId}`, {
        quantity: currentQty + 10
      })
      await handleDismiss(notificationId)
    } catch (err: any) {
      console.error('Quick restock failed', err)
      setActionError(err?.response?.data?.message || err?.message || 'Restock failed')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleStartPrep = async (notification: Notification) => {
    const orderId = orderIdFromNotification(notification)
    if (!orderId) {
      setActionError('Missing order id for this alert')
      return
    }
    setActionLoadingId(notification.id)
    setActionError(null)
    try {
      await api.patch(`/orders/${orderId}/status`, {
        status: 'preparation_in_progress'
      })
      dismissIds([notification.id])
    } catch (err: any) {
      console.error('Start prep failed', err)
      const status = err?.response?.status
      setActionError(
        status === 403
          ? 'You need orders:write to start prep'
          : err?.response?.data?.message || err?.message || 'Start prep failed'
      )
    } finally {
      setActionLoadingId(null)
    }
  }

  const getCardStyles = (severity?: string) => {
    const s = severity?.toUpperCase()
    if (s === 'CRITICAL') {
      return {
        border: 'border-l-rose-500 dark:border-l-rose-500/80',
        bg: 'bg-rose-500/5 hover:bg-rose-500/10 dark:bg-rose-500/[0.02] dark:hover:bg-rose-500/[0.05]',
        badge: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
      }
    } else if (s === 'WARNING') {
      return {
        border: 'border-l-amber-500 dark:border-l-amber-500/80',
        bg: 'bg-amber-500/5 hover:bg-amber-500/10 dark:bg-amber-500/[0.02] dark:hover:bg-amber-500/[0.05]',
        badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
      }
    } else {
      return {
        border: 'border-l-violet-500 dark:border-l-violet-500/80',
        bg: 'bg-violet-500/5 hover:bg-violet-500/10 dark:bg-violet-500/[0.02] dark:hover:bg-violet-500/[0.05]',
        badge: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20'
      }
    }
  }

  const getNotificationIcon = (code?: string) => {
    const c = code?.toUpperCase()
    if (c === 'STOCK_LOW' || c === 'LOW_STOCK') return <Package size={14} className="text-rose-500" />
    if (c === 'SHIFT_UNASSIGNED') return <Clock size={14} className="text-amber-500" />
    if (c === 'MISSING_WAGE_RATE') return <Users size={14} className="text-violet-500" />
    if (c === 'PENDING_ORDER') return <ClipboardList size={14} className="text-rose-500" />
    if (c === 'DELIVERY_ACCEPTED') return <Truck size={14} className="text-emerald-500" />
    if (c === 'BOOKING_UNASSIGNED') return <Calendar size={14} className="text-rose-500" />
    if (c === 'MERCHANT_APPLICATION_PENDING') return <Building2 size={14} className="text-amber-500" />
    return <Bell size={14} className="text-slate-400" />
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen)
          setActionError(null)
          fetchNotifications()
        }}
        className="relative p-2 rounded-lg text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800/50 transition-all"
        title="Notifications"
      >
        <Bell size={18} />
        {activeNotifications.length > 0 && (
          // The badge says there is something new; the bell stays still. Keyed on the count,
          // so it fades in once when the number changes (never loops, and not at all under
          // reduced motion).
          <span
            key={activeNotifications.length}
            className="absolute top-0.5 right-0.5 min-w-5 h-5 px-1 bg-gradient-to-r from-rose-500 to-red-600 text-white text-[11px] leading-none font-bold rounded-full flex items-center justify-center ring-2 ring-white dark:ring-[#0c0c0f] motion-safe:animate-fade-in"
          >
            {activeNotifications.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2.5 w-80 sm:w-96 rounded-2xl bg-white dark:bg-[#0c0c0f] border border-slate-200 dark:border-zinc-800 shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/10">
            <div className="flex items-center gap-2">
              <span className="font-display font-semibold text-sm text-slate-900 dark:text-white">
                {isPlatformAdminView ? 'Platform Alerts' : 'Smart Action Center'}
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-400">
                {activeNotifications.length} Tasks
              </span>
            </div>
            {activeNotifications.length > 0 && (
              <button
                type="button"
                onClick={handleDismissAll}
                disabled={dismissingAll}
                className="text-xs font-semibold text-slate-400 hover:text-slate-700 dark:hover:text-zinc-300 transition-colors disabled:opacity-50"
              >
                {dismissingAll ? 'Dismissing…' : 'Dismiss All'}
              </button>
            )}
          </div>

          {actionError && (
            <div className="px-4 py-2 text-xs font-medium text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border-b border-rose-100 dark:border-rose-900/40">
              {actionError}
            </div>
          )}

          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100 dark:divide-zinc-800">
            {loadState === 'failed' && (
              <ErrorState
                title="Couldn't load alerts"
                text="Tasks that need you may be missing here until they load."
                onRetry={retryLoad}
                retrying={retrying}
              />
            )}
            {loadState === 'loading' && activeNotifications.length === 0 && (
              <div role="status" className="flex items-center justify-center gap-2 py-12 text-xs text-slate-500 dark:text-zinc-400">
                <Loader2 size={14} className="animate-spin" />
                Loading alerts…
              </div>
            )}
            {loadState === 'loaded' && activeNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-zinc-900/40 flex items-center justify-center mb-3">
                  <Check className="text-emerald-500" size={20} />
                </div>
                <span className="font-display font-semibold text-sm text-slate-900 dark:text-white">All caught up!</span>
                <span className="text-xs text-slate-400 dark:text-zinc-500 mt-1 max-w-[200px]">
                  {isPlatformAdminView
                    ? 'No pending platform alerts.'
                    : 'There are no pending alerts or smart reminders requiring action.'}
                </span>
              </div>
            ) : (
              activeNotifications.map(n => {
                const styles = getCardStyles(n.errorCode === 404 || n.workflowCode === 'BOOKING_UNASSIGNED' ? 'CRITICAL' : (n.workflowCode === 'SHIFT_UNASSIGNED' ? 'WARNING' : 'INFO'))
                const resolveTo = resolveTaskPath(n)
                return (
                  <div
                    key={n.id}
                    className={`flex flex-col p-4 border-l-4 transition-all duration-200 ${styles.border} ${styles.bg}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-white dark:bg-zinc-900 border border-slate-200/50 dark:border-zinc-800/40 shadow-sm shrink-0">
                          {getNotificationIcon(n.workflowCode)}
                        </div>
                        <span className="font-semibold text-xs text-slate-950 dark:text-white">
                          {n.title}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDismiss(n.id)}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300 p-0.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
                        title="Dismiss alert"
                      >
                        <X size={13} />
                      </button>
                    </div>

                    <p className="text-xs text-slate-600 dark:text-zinc-400 mt-2 leading-relaxed font-normal">
                      {n.internalMessage || n.externalMessage}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 mt-3.5 pt-2.5 border-t border-slate-200/20 dark:border-zinc-800/30">
                      {resolveTo && (
                        <Link
                          to={resolveTo}
                          onClick={() => setIsOpen(false)}
                          className="text-[10px] font-bold text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 flex items-center gap-1 transition-all mr-auto hover:translate-x-0.5"
                        >
                          Resolve Task ➔
                        </Link>
                      )}

                      {n.workflowCode === 'STOCK_LOW' && n.metadata && (
                        <button
                          type="button"
                          onClick={() => handleQuickRestock(
                            n.id,
                            n.metadata?.storeId || '',
                            n.metadata?.inventoryId || '',
                            n.metadata?.quantity || 0
                          )}
                          disabled={actionLoadingId === n.id}
                          className="text-[11px] font-bold px-2.5 py-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg transition-all flex items-center gap-1 shadow-sm"
                        >
                          {actionLoadingId === n.id ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <span>+10 Stock</span>
                          )}
                        </button>
                      )}

                      {n.workflowCode === 'PENDING_ORDER' && (
                        <button
                          type="button"
                          onClick={() => handleStartPrep(n)}
                          disabled={actionLoadingId === n.id}
                          className="text-[11px] font-bold px-2.5 py-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg transition-all flex items-center gap-1 shadow-sm"
                        >
                          {actionLoadingId === n.id ? (
                            <>
                              <Loader2 size={10} className="animate-spin" />
                              <span>Starting…</span>
                            </>
                          ) : (
                            <>
                              <Play size={8} className="fill-white" />
                              <span>Start Prep</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
